import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
    Box,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Tooltip,
    TextField,
    useTheme,
    Typography,
    Switch,
    FormControlLabel,
} from '@mui/material';
import CatchingPokemonIcon from '@mui/icons-material/CatchingPokemon';
import gotcha_own from "../images/catched_ball.png";
import hundred_own from "../images/100_ball.png";
import shiny_own from "../images/shiny_ball.png";
import crypto_own from "../images/crypto_ball.png";
import canceled from "../images/cancel.png";
import checked from "../images/checked.png";
import dyna from "../images/dyna.png";
import individuell from "../images/individuell_ball.png";
import PokemonImage from '../Components/PokemonImage';

// Debounce-Hilfsfunktion
const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            func(...args);
        }, delay);
    };
};

const Pokedex = () => {
    const theme = useTheme();
    const darkMode = theme.palette.mode === "dark";

    const [pokemonData, setPokemonData] = useState({});
    const [loading, setLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);
    const [error, setError] = useState(null);
    const [visibleGeneration, setVisibleGeneration] = useState('GEN1');
    const [userId, setUserId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showShiny, setShowShiny] = useState(false);
    const [fieldStates, setFieldStates] = useState({});

    // Refs für den Zugriff auf den aktuellen Zustand in Callbacks
    const fieldStatesRef = useRef(fieldStates);
    useEffect(() => { fieldStatesRef.current = fieldStates; }, [fieldStates]);
    const pokemonDataRef = useRef(pokemonData);
    useEffect(() => { pokemonDataRef.current = pokemonData; }, [pokemonData]);
    const userIdRef = useRef(userId);
    useEffect(() => { userIdRef.current = userId; }, [userId]);

    const generations = ['ALLE', 'GEN1', 'GEN2', 'GEN3', 'GEN4', 'GEN5', 'GEN6', 'GEN7', 'GEN8', 'GEN9'];

    // --- 1. Effekt: User-ID beim initialen Laden abrufen ---
    useEffect(() => {
        const fetchUserId = async () => {
            const token = localStorage.getItem('token');
            try {
                const response = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
                });
                if (response?.data?.user_id) {
                    setUserId(response.data.user_id);
                } else {
                    setError('Benutzer-ID konnte nicht geladen werden. Bitte melde dich erneut an.');
                    setLoading(false);
                }
            } catch (err) {
                console.error('Error fetching user ID:', err);
                setError('Fehler beim Laden deiner Benutzerdaten. Ist der Server erreichbar?');
                setLoading(false);
            }
        };
        fetchUserId();
    }, []);

    // --- Hilfsfunktion zum Abrufen von Pokémon-Daten für eine spezifische Generation ---
    const fetchGenerationData = useCallback(async (generation, currentUserId) => {
        if (!currentUserId) {
            console.warn("User ID not available for data fetch.");
            return null;
        }
        try {
            const response = await axios.get(`/api/pokedex?generation=${generation}&user_id=${currentUserId}`);
            const fetchedPokemon = response.data;

            setFieldStates(prevStates => {
                const newStates = { ...prevStates };
                fetchedPokemon.forEach(p => {
                    ['catched', 'shiny', 'perfect', 'crypto', 'dynamax', 'individual'].forEach(field => {
                        newStates[`${p.dex_id}-${field}`] = p[field] ?? false;
                    });
                });
                return newStates;
            });

            setPokemonData(prev => ({ ...prev, [generation]: fetchedPokemon }));
            return fetchedPokemon;
        } catch (err) {
            console.error(`Fehler beim Laden der Pokédex-Daten für ${generation}:`, err);
            setError(`Fehler beim Laden der Daten für ${generation}.`);
            return null;
        }
    }, []);

    // --- 2. Effekt: Initiales Laden ALLER Pokémon-Daten, sobald userId verfügbar ist ---
    useEffect(() => {
        const loadAllPokemonInitially = async () => {
            if (!userId) {
                setLoading(false);
                return;
            }

            const allGenerationsLoaded = generations.slice(1).every(gen => pokemonDataRef.current[gen]);
            if (allGenerationsLoaded && Object.keys(pokemonDataRef.current).length > 0) {
                 setLoading(false);
                 return;
            }

            setLoading(true);
            setError(null);

            try {
                const promises = generations.slice(1).map(async (gen) => {
                    if (!pokemonDataRef.current[gen]) {
                        await fetchGenerationData(gen, userId);
                    }
                });
                await Promise.all(promises);
            } catch (err) {
                console.error('Fehler beim initialen Laden aller Daten:', err);
                setError('Fehler beim Laden der Daten.');
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            loadAllPokemonInitially();
        }
    }, [userId, fetchGenerationData]);

    // --- Handler für den Wechsel der Generation ---
    const handleGenerationChange = async (event) => {
        const newGeneration = event.target.value;

        if (newGeneration === visibleGeneration) {
            return;
        }

        setVisibleGeneration(newGeneration);

        setFilterLoading(true);
        setError(null);

        try {
            if (newGeneration === 'ALLE') {
                const promises = generations.slice(1).map(async (gen) => {
                    if (!pokemonDataRef.current[gen]) {
                        await fetchGenerationData(gen, userId);
                    }
                });
                await Promise.all(promises);
            } else {
                if (!pokemonDataRef.current[newGeneration]) {
                    await fetchGenerationData(newGeneration, userId);
                }
            }
            await new Promise(resolve => setTimeout(resolve, 300));

        } catch (err) {
            console.error(`Fehler beim Laden der Pokédex-Daten für ${newGeneration} nach Filterwechsel:`, err);
            setError(`Fehler beim Laden der Daten für ${newGeneration}.`);
        } finally {
            setFilterLoading(false);
        }
    };

    // --- Funktion zum Senden von Besitz-Updates an das Backend ---
    const sendOwnershipUpdateToBackend = async (dexId, field, updatedValue) => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('Kein Token vorhanden – bitte einloggen.');
            setFieldStates(prev => ({
                ...prev,
                [`${dexId}-${field}`]: !updatedValue
            }));
            const currentPokemonData = pokemonDataRef.current;
            const originalPokemon = Object.values(currentPokemonData).flat().find(p => p.dex_id === dexId);
            if (originalPokemon) {
                setPokemonData(prev => ({
                    ...prev,
                    [originalPokemon.generation]: (prev[originalPokemon.generation] || []).map(p =>
                        p.dex_id === dexId ? { ...p, [field]: !updatedValue } : p
                    ),
                }));
            }
            return;
        }

        try {
            const currentFieldStates = fieldStatesRef.current;
            const currentPokemonData = pokemonDataRef.current;
            const currentUserId = userIdRef.current;

            if (!currentUserId) {
                console.error('Keine gültige Benutzer-ID für den Payload gefunden.');
                setFieldStates(prev => ({
                    ...prev,
                    [`${dexId}-${field}`]: !updatedValue
                }));
                const originalPokemon = Object.values(currentPokemonData).flat().find(p => p.dex_id === dexId);
                if (originalPokemon) {
                    setPokemonData(prev => ({
                        ...prev,
                        [originalPokemon.generation]: (prev[originalPokemon.generation] || []).map(p =>
                            p.dex_id === dexId ? { ...p, [field]: !updatedValue } : p
                        ),
                    }));
                }
                return;
            }

            const originalPokemon = Object.values(currentPokemonData)
                .flat()
                .find(p => p.dex_id === dexId);

            if (!originalPokemon) {
                console.error(`Pokemon mit dex_id ${dexId} nicht gefunden im pokemonDataRef.`);
                setFieldStates(prev => ({
                    ...prev,
                    [`${dexId}-${field}`]: !updatedValue
                }));
                const originalPokemonFallback = Object.values(currentPokemonData).flat().find(p => p.dex_id === dexId);
                if (originalPokemonFallback) {
                    setPokemonData(prev => ({
                        ...prev,
                        [originalPokemonFallback.generation]: (prev[originalPokemonFallback.generation] || []).map(p =>
                            p.dex_id === dexId ? { ...p, [field]: !updatedValue } : p
                        ),
                    }));
                }
                return;
            }

            const payload = {
                user_id: currentUserId,
                dex_id: dexId,
                catched: currentFieldStates[`${dexId}-catched`] ?? false,
                shiny: currentFieldStates[`${dexId}-shiny`] ?? false,
                perfect: currentFieldStates[`${dexId}-perfect`] ?? false,
                crypto: currentFieldStates[`${dexId}-crypto`] ?? false,
                dynamax: currentFieldStates[`${dexId}-dynamax`] ?? false,
                individual: currentFieldStates[`${dexId}-individual`] ?? false,
            };

            await axios.post(
                '/api/users/add_pokemon_owned',
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setPokemonData(prev => ({
                ...prev,
                [originalPokemon.generation]: (prev[originalPokemon.generation] || []).map(p =>
                    p.dex_id === dexId ? { ...p, [field]: updatedValue } : p
                ),
            }));

        } catch (err) {
            console.error('Fehler beim Aktualisieren der Pokémon-Daten:', err);
            alert("Fehler beim Aktualisieren der Pokémon-Daten. Bitte Seite neu laden.");

            setFieldStates(prev => ({
                ...prev,
                [`${dexId}-${field}`]: !updatedValue
            }));

            const currentPokemonData = pokemonDataRef.current;
            const originalPokemon = Object.values(currentPokemonData)
                .flat()
                .find(p => p.dex_id === dexId);
            if (originalPokemon) {
                setPokemonData(prev => ({
                    ...prev,
                    [originalPokemon.generation]: (prev[originalPokemon.generation] || []).map(p =>
                        p.dex_id === dexId ? { ...p, [field]: !updatedValue } : p
                    ),
                }));
            }
        }
    };

    const debouncedUpdateOwnership = useCallback(
        debounce(sendOwnershipUpdateToBackend, 300),
        []
    );

    const getTypeColor = (type) => {
        const colors = {
            Pflanze: '#78C850', Gift: '#A040A0', Wasser: '#6890F0',
            Boden: '#E0C068', Gestein: '#B8A038', Unlicht: '#705848',
            Fee: '#EE99AC', Feuer: '#F08030', Käfer: '#A8B820', Flug: '#A890F0'
        };
        return colors[type] || '#AAA';
    };

    // Pokémon filtern und sortieren
    const filteredPokemon = (() => {
        let pokemonList;
        if (visibleGeneration === 'ALLE') {
            // Holen Sie sich die Generationen in der gewünschten Reihenfolge (z.B. GEN1, GEN2, ...)
            const sortedGenerations = generations.slice(1).sort((a, b) => {
                const numA = parseInt(a.replace('GEN', ''));
                const numB = parseInt(b.replace('GEN', ''));
                return numA - numB;
            });

            // Sammeln und flachen der Pokémon-Daten in der richtigen Generationsreihenfolge
            pokemonList = sortedGenerations.flatMap(gen => pokemonData[gen] || []);

            // Zusätzliche Sortierung innerhalb jeder Generation nach dex_id, falls nötig
            pokemonList.sort((a, b) => a.dex_id - b.dex_id);

        } else {
            pokemonList = pokemonData[visibleGeneration] || [];
        }

        // Filter nach Suchbegriff
        return pokemonList.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
    })();

    // --- Rendering-Logik ---
    if (loading) return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
            <CatchingPokemonIcon sx={{ fontSize: 64, animation: 'bounce 1.2s infinite' }} color="primary" />
            <Typography variant="h6">Lade initiale Daten...</Typography>
            <style>
                {`@keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }`}
            </style>
        </Box>
    );

    if (error) return <Typography color="error" sx={{ textAlign: 'center', mt: 4 }}>{error}</Typography>;

    return (
        <Box
            sx={{
                padding: 2,
                paddingBottom: { xs: "96px", sm: "64px", md: "120px" }
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2, maxWidth: 400, mx: 'auto' }}>
                <FormControl fullWidth>
                    <InputLabel>Generation</InputLabel>
                    <Select
                        value={visibleGeneration}
                        label="Generation"
                        onChange={handleGenerationChange}
                    >
                        {generations.map((gen) => (
                            <MenuItem key={gen} value={gen}>{gen}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Suche Pokémon"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FormControlLabel
                    control={<Switch checked={showShiny} onChange={(e) => setShowShiny(e.target.checked)} />}
                    label="Shiny anzeigen"
                />
            </Box>

            {filterLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
                    <CatchingPokemonIcon sx={{ fontSize: 64, animation: 'bounce 1.2s infinite' }} color="primary" />
                    <Typography variant="h6" sx={{ mt: 2 }}>Lade Pokémon nach Filter...</Typography>
                    <style>
                        {`@keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }`}
                    </style>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
                    {filteredPokemon.map(pokemon => (
                        <Box
                            key={pokemon.dex_id}
                            sx={{
                                width: 150,
                                border: '1px solid #ccc',
                                borderRadius: 2,
                                padding: 1,
                                backgroundColor: darkMode ? '#1e1e1e' : '#fafafa',
                                textAlign: 'center'
                            }}
                        >
                            <Typography variant="subtitle1" noWrap>{pokemon.name}</Typography>
                            <PokemonImage pokemon={pokemon} showShiny={showShiny} />
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {[pokemon.first_type, pokemon.second_type].filter(Boolean).map((type, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            backgroundColor: getTypeColor(type),
                                            color: 'white',
                                            borderRadius: 1,
                                            px: 1,
                                            fontSize: '0.75rem'
                                        }}
                                    >
                                        {type}
                                    </Box>
                                ))}
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', borderCollapse: 'collapse' }}>
                                {[
                                    { field: 'catched', icon: gotcha_own },
                                    { field: 'shiny', icon: shiny_own },
                                    { field: 'perfect', icon: hundred_own },
                                    { field: 'crypto', icon: crypto_own },
                                    { field: 'dynamax', icon: dyna },
                                    { field: 'individual', icon: individuell },
                                ].map(({ field, icon }, idx) => {
                                    const isChecked = fieldStates[`${pokemon.dex_id}-${field}`] ?? false;

                                    return (
                                        <Tooltip key={field} title={field.charAt(0).toUpperCase() + field.slice(1)} arrow>
                                            <Box
                                                onClick={() => {
                                                    const newCheckedValue = !isChecked;

                                                    setFieldStates(prev => ({
                                                        ...prev,
                                                        [`${pokemon.dex_id}-${field}`]: newCheckedValue
                                                    }));

                                                    debouncedUpdateOwnership(pokemon.dex_id, field, newCheckedValue);
                                                }}
                                                sx={{
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexDirection: 'column',
                                                    padding: 1,
                                                    borderRight: idx % 2 === 0 ? '1px solid #ddd' : 'none',
                                                    borderBottom: idx < 4 ? '1px solid #ddd' : 'none'
                                                }}
                                            >
                                                <Box component="img" src={icon} alt={field} sx={{ width: 20, height: 20 }} />
                                                <Box
                                                    component="img"
                                                    src={isChecked ? checked : canceled}
                                                    sx={{ width: 14, height: 14, mt: 0.5 }}
                                                />
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default Pokedex;